/**
 * The three page-level frames: the container, the header, and the hero.
 *
 * Each route had set its own max width and padding, so the content column
 * shifted between screens. `SectionCard` was also standing in for a page title,
 * which put an `h2` where the page's `h1` belonged.
 *
 * @module components/ui/Page
 */

import React, { type ReactNode } from "react";

import { Eyebrow, Heading, MutedText } from "./Text";

export interface PageContainerProps {
  children: ReactNode;
  className?: string;
  "data-testid"?: string;
}

/** The content column every routed screen sits in. */
export function PageContainer({
  children,
  className = "",
  "data-testid": testId,
}: PageContainerProps): React.ReactElement {
  return (
    <div
      className={`mx-auto w-full max-w-[1380px] p-8 max-lg:px-4 max-lg:py-5 max-sm:px-3 ${className}`.trim()}
      data-testid={testId}
    >
      {children}
    </div>
  );
}

export interface PageHeaderProps {
  title: string;
  /** 2 for a screen shown under another's `h1`, such as a programme's. */
  level?: 1 | 2;
  /** One sentence on what this screen is for. May hold badges, so it renders in a `div`. */
  description?: ReactNode;
  /** Small uppercase label above the title. */
  eyebrow?: string;
  /**
   * The screen's actions. One primary at most; everything else steps down.
   */
  actions?: ReactNode;
  className?: string;
  "data-testid"?: string;
}

/**
 * The title block at the top of a routed screen. Renders the page's only `h1`,
 * or an `h2` at the same size under a layout that already has one.
 */
export function PageHeader({
  title,
  level = 1,
  description = undefined,
  eyebrow = undefined,
  actions = undefined,
  className = "",
  "data-testid": testId,
}: PageHeaderProps): React.ReactElement {
  return (
    <div
      className={`flex flex-wrap items-start justify-between gap-4 ${className}`.trim()}
      data-testid={testId}
    >
      <div className="min-w-0">
        {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
        <Heading level={level} size="display">
          {title}
        </Heading>
        {description && (
          <MutedText as="div" className="mt-1 max-w-3xl">
            {description}
          </MutedText>
        )}
      </div>
      {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

export interface HeroPanelProps {
  title: string;
  eyebrow?: string;
  description?: ReactNode;
  /** Badges naming the thing: award type, level, credits. */
  meta?: ReactNode;
  /** Status and provenance, opposite the title. */
  aside?: ReactNode;
  /** 1 when the hero is the page's subject; 2 under a layout that titles itself. */
  headingLevel?: 1 | 2;
  className?: string;
  "data-testid"?: string;
}

/**
 * The panel that opens a screen about one thing, naming it and its state.
 *
 * Distinct from `PageHeader`, which titles a screen about many things.
 */
export function HeroPanel({
  title,
  eyebrow = undefined,
  description = undefined,
  meta = undefined,
  aside = undefined,
  headingLevel = 1,
  className = "",
  "data-testid": testId,
}: HeroPanelProps): React.ReactElement {
  return (
    <section
      className={`mb-8 rounded-box border border-base-300 bg-base-200 p-8 max-sm:p-5 ${className}`.trim()}
      data-testid={testId}
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
          <Heading level={headingLevel} size="display">
            {title}
          </Heading>
          {description && (
            <MutedText as="div" measure className="mt-2">
              {description}
            </MutedText>
          )}
        </div>
        {aside && <div className="shrink-0 text-right">{aside}</div>}
      </div>
      {meta && <div className="mt-6 flex flex-wrap items-center gap-2">{meta}</div>}
    </section>
  );
}
